<script lang="ts" setup>
import { PropType } from 'vue';
import { ContactModel, Permission } from '@/types';
import { MailIcon, PhoneIcon } from '@heroicons/vue/solid';
import { PencilAltIcon } from '@heroicons/vue/outline';
import UiAuditInfo from '@/components/UiAuditInfo.vue';
import { formatDateAsString } from '@/utils/datetime';
import { applyPhoneMask } from '@/utils/number';
import { ContactSentiment, contactSentimentMap } from '@/utils/contactSentiment';
import { ContactHealthScoreBadge } from '.';
import { ContactHealthScoreStats } from '@/types/metrics/ContactHealthScore';

const props = defineProps({
    modelValue: {
        type: Object as PropType<ContactModel>,
        required: true,
    },
});

const sentiment = (() => {
    const lastSentiment = props.modelValue.lastSentiment;
    if (!lastSentiment) return null;
    return contactSentimentMap[lastSentiment as ContactSentiment];
})();

const healthScore: ContactHealthScoreStats = {
    score: 59,
    analysis:
        'A relação com a Fintrust Capital vinha estável sob tratativas de integração e renovação contratual, mas sofreu uma queda recente de satisfação que reduziu o health score para 59. O principal ponto de atrito foi uma indisponibilidade de 40 minutos no sistema em horário de pico, o que gerou forte insatisfação e a exigência de reembolso e de um relatório de causa raiz. Como próxima ação recomendada, deve-se formalizar o envio do relatório técnico de causa raiz e conceder o crédito na fatura solicitado para restabelecer a confiança do cliente antes da renovação de fim de ano.',
};
</script>

<template>
    <UiPanel class="hover:bg-white">
        <div class="flex items-center justify-between">
            <div class="flex flex-1 items-center">
                <component
                    :is="$can(Permission.VIEW_CONTACT) ? 'RouterLink' : 'span'"
                    :to="{
                        name: 'ViewContactById',
                        params: {
                            id: modelValue.id,
                        },
                    }"
                    class="text-md text-primary truncate font-medium"
                >
                    {{ modelValue.name }}
                </component>
                <UiIsActiveBadge :model-value="modelValue" class="ml-2 text-xs" />
            </div>
            <div class="mr-2 flex space-x-1">
                <ContactHealthScoreBadge :health-score="healthScore" />
            </div>
            <div class="mr-2 flex space-x-1">
                <router-link
                    v-if="$can(Permission.VIEW_CONTACT_TIMELINE)"
                    :to="{
                        name: 'ViewContactTimelineById',
                        params: {
                            id: modelValue.id,
                        },
                    }"
                >
                    <UiButton variant="white" text-variant="primary">
                        {{ $t('contact.list.actions.viewTimeline') }}
                    </UiButton>
                </router-link>
            </div>
            <div v-if="$can(Permission.EDIT_CONTACT)" class="flex space-x-1">
                <UiActionButton
                    v-tippy="$t('core.actions.Edit')"
                    variant="primary"
                    class="rounded-full"
                    :to="{
                        name: 'EditContactById',
                        params: {
                            id: modelValue.id,
                        },
                    }"
                >
                    <span class="sr-only"> {{ $t('core.actions.Edit') }} </span>
                    <PencilAltIcon class="h-6 w-6" aria-hidden="true" />
                </UiActionButton>
            </div>
        </div>
        <div class="mt-2 sm:flex sm:justify-between">
            <div class="sm:flex sm:space-x-4">
                <p class="flex items-center text-sm text-gray-500">
                    <MailIcon
                        class="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                        aria-hidden="true"
                    />
                    {{
                        modelValue.emails.find((e) => e.isMain)?.email || $t('contact.list.noEmail')
                    }}
                </p>
                <p class="flex items-center text-sm text-gray-500 sm:ml-0 sm:mt-0">
                    <PhoneIcon
                        class="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                        aria-hidden="true"
                    />
                    {{ applyPhoneMask(modelValue.phones.find((p) => p.isMain)!.number) || '' }}
                </p>
            </div>
        </div>
        <div class="mt-2 flex flex-wrap justify-between border-t pt-3">
            <div class="mb-1 grid gap-3 sm:mb-0 sm:grid-cols-3">
                <div class="flex items-center space-x-1 text-xs text-gray-600">
                    <span>{{ $t('contact.list.owner') }}: </span>
                    <div class="ml-1">
                        {{ modelValue.ownerUser.name }}
                    </div>
                </div>
                <div class="flex items-center space-x-1 text-xs text-gray-600">
                    <span>{{ $t('contact.list.lastInteraction') }}: </span>
                    <template v-if="modelValue.lastInteraction">
                        <span class="truncate">{{
                            formatDateAsString(modelValue.lastInteraction?.toString(), 'DD/MM/YYYY')
                        }}</span>
                        <span class="truncate">{{
                            formatDateAsString(modelValue.lastInteraction?.toString(), 'HH:mm:ss')
                        }}</span>
                    </template>
                    <span v-else>-</span>
                </div>
                <div class="flex items-center space-x-1 text-xs text-gray-600">
                    <template v-if="sentiment">
                        <span>{{ sentiment.face }} </span>
                        <span>{{ $t(sentiment.label) }}</span>
                    </template>
                </div>
            </div>
            <UiAuditInfo :model-value="modelValue" />
        </div>
    </UiPanel>
</template>
